'use client';

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/apiClient';

/**
 * Shape served from GET /api/v1/public/settings:
 * { branding: {...}, seo: {...}, contact: {...} }
 *
 * Fallback mirrors the values hardcoded in the public site before the
 * settings module existed — used while the request is in flight or if
 * the API is unreachable. The fallback mirrors SettingsDefaultSeeder.
 */
export const PUBLIC_SETTINGS_FALLBACK = {
  branding: {
    site_name:  'Centera Tech',
    tagline:    'Transforming Ideas into Digital Reality',
    logo_light: '/images/logo-light.svg',
    logo_dark:  '/images/logo-dark.svg',
    logo:       '/images/logo.svg',
    favicon:    '/favicon.ico',
  },
  seo: {
    default_title:       'Centera Tech — Transforming Ideas into Digital Reality',
    title_template:      '%s | Centera Tech',
    default_description: 'Centera Tech is a software development company specializing in web development, mobile apps, and IT services. We transform ideas into digital reality.',
    default_keywords:    'Centera Tech, software development, web development, mobile apps, IT services, digital solutions',
    og_description:      'Centera Tech is a software development company specializing in web development, mobile apps, and IT services.',
    og_image:            '/og-image.png',
    canonical_url:       'https://centera.tech',
  },
  contact: {
    email:     'support@centera.tech',
    phone:     '+91 9209155153',
    address:   'Office no. 204, Eden Garden Commercial Complex, Kolhapur - 416003, Maharashtra, India',
    map_url:   'https://www.google.com/maps?ll=16.707947,74.228303&z=15&t=h&hl=en&gl=IN&mapclient=embed&cid=1583718635517808008',
    facebook:  'https://www.facebook.com/profile.php?id=61571785329732',
    twitter:   'https://x.com/centeratech',
    instagram: 'https://www.instagram.com/centera.tech',
    youtube:   'https://www.youtube.com/@CenteraTech',
    linkedin:  'https://www.linkedin.com/in/centera-tech-72ba35347',
    github:    '',
  },
};

const mergeWithFallback = (data) => ({
  branding: { ...PUBLIC_SETTINGS_FALLBACK.branding, ...(data?.branding || {}) },
  seo:      { ...PUBLIC_SETTINGS_FALLBACK.seo,      ...(data?.seo      || {}) },
  contact:  { ...PUBLIC_SETTINGS_FALLBACK.contact,  ...(data?.contact  || {}) },
});

export const fetchPublicSettings = async () => {
  const res = await apiClient.get('public/settings');
  return res.data?.data || {};
};

export const usePublicSettings = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['public-settings'],
    queryFn: fetchPublicSettings,
    staleTime: 5 * 60 * 1000,  // 5 minutes
    gcTime: 30 * 60 * 1000,    // 30 minutes
    retry: 1,
  });

  return {
    settings: mergeWithFallback(data),
    isLoading,
    error,
  };
};

export default usePublicSettings;
